import React from 'react';
import type { z } from 'zod';

export type ZodFormErrors<T> = Partial<Record<keyof T, string>>;
export type ZodFormTouched<T> = Partial<Record<keyof T, boolean>>;

export interface UseZodFormOptions<T extends Record<string, unknown>> {
  schema: z.ZodType<T>;
  initialValues: T;
  /**
   * Recebe os valores já normalizados pelo schema (trim, coerções etc.),
   * não os valores crus do input. Pode ser assíncrono — `isSubmitting`
   * acompanha a promise.
   */
  onSubmit: (values: T) => void | Promise<void>;
}

export interface ZodFieldProps<V> {
  name: string;
  value: V;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
}

/**
 * Formulário controlado com validação por schema zod.
 *
 * O projeto não tem (nem quer) uma biblioteca de formulário: Poll e NewsForm
 * são `useState` na mão. Este hook é o meio-termo — mantém o formato
 * controlado de sempre e só terceiriza a validação para o zod, devolvendo
 * `errors` no formato que o `MPFInput` já consome (`errorText`).
 *
 * Estratégia de validação, na ordem em que o usuário percebe:
 * 1. digitando pela primeira vez — silêncio (nada de erro em campo intocado);
 * 2. ao sair do campo (`blur`) — marca como tocado e valida;
 * 3. depois de tocado — revalida a cada tecla, para o erro sumir assim que
 *    for corrigido;
 * 4. no submit — marca tudo como tocado e mostra todos os erros de uma vez.
 */
export function useZodForm<T extends Record<string, unknown>>({
  schema,
  initialValues,
  onSubmit,
}: UseZodFormOptions<T>) {
  const [values, setValues] = React.useState<T>(initialValues);
  const [errors, setErrors] = React.useState<ZodFormErrors<T>>({});
  const [touched, setTouched] = React.useState<ZodFormTouched<T>>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitCount, setSubmitCount] = React.useState(0);

  const validate = React.useCallback(
    (candidate: T) => {
      const result = schema.safeParse(candidate);
      if (result.success) {
        return { errors: {} as ZodFormErrors<T>, data: result.data };
      }
      const fieldErrors = result.error.flatten().fieldErrors as Record<
        string,
        string[] | undefined
      >;
      const next: ZodFormErrors<T> = {};
      Object.entries(fieldErrors).forEach(([field, messages]) => {
        if (messages?.length) {
          next[field as keyof T] = messages[0];
        }
      });
      return { errors: next, data: undefined };
    },
    [schema],
  );

  // Só deixa passar o erro de campos já tocados: validar o objeto inteiro é
  // barato, mas mostrar tudo de uma vez antes do submit é hostil.
  const applyVisibleErrors = React.useCallback(
    (allErrors: ZodFormErrors<T>, visibleIn: ZodFormTouched<T>) => {
      const next: ZodFormErrors<T> = {};
      (Object.keys(allErrors) as Array<keyof T>).forEach((field) => {
        if (visibleIn[field]) next[field] = allErrors[field];
      });
      setErrors(next);
    },
    [],
  );

  const setFieldValue = React.useCallback(
    (field: keyof T, value: T[keyof T]) => {
      setValues((current) => {
        const next = { ...current, [field]: value };
        applyVisibleErrors(validate(next).errors, touched);
        return next;
      });
    },
    [applyVisibleErrors, touched, validate],
  );

  const handleBlur = React.useCallback(
    (field: keyof T) => {
      const nextTouched = { ...touched, [field]: true };
      setTouched(nextTouched);
      applyVisibleErrors(validate(values).errors, nextTouched);
    },
    [applyVisibleErrors, touched, validate, values],
  );

  const getFieldProps = React.useCallback(
    <K extends keyof T>(field: K): ZodFieldProps<T[K]> => ({
      name: String(field),
      value: values[field],
      onChange: (event) =>
        setFieldValue(field, event.target.value as unknown as T[keyof T]),
      onBlur: () => handleBlur(field),
    }),
    [handleBlur, setFieldValue, values],
  );

  /** Para o servidor devolver erro por campo (HTTP 400 com `fields`). */
  const setFieldError = React.useCallback((field: keyof T, message: string) => {
    setTouched((current) => ({ ...current, [field]: true }));
    setErrors((current) => ({ ...current, [field]: message }));
  }, []);

  const reset = React.useCallback(
    (nextValues: T = initialValues) => {
      setValues(nextValues);
      setErrors({});
      setTouched({});
      setSubmitCount(0);
    },
    [initialValues],
  );

  const handleSubmit = React.useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault();
      if (isSubmitting) return;

      setSubmitCount((count) => count + 1);

      const { errors: allErrors, data } = validate(values);
      const allTouched = (Object.keys(values) as Array<keyof T>).reduce(
        (acc, field) => ({ ...acc, [field]: true }),
        {} as ZodFormTouched<T>,
      );
      setTouched(allTouched);
      setErrors(allErrors);

      if (!data) return;

      setIsSubmitting(true);
      try {
        await onSubmit(data);
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, onSubmit, validate, values],
  );

  const hasErrors = Object.keys(errors).length > 0;

  return {
    values,
    errors,
    touched,
    hasErrors,
    isSubmitting,
    submitCount,
    getFieldProps,
    setFieldValue,
    setFieldError,
    handleSubmit,
    reset,
  };
}

export default useZodForm;
